import { formatMoney } from './currency';
import { getListingQuantityValue, toPositiveInteger } from './listingQuantity';
import { types as sdkTypes } from './sdkLoader';
import { LISTING_UNIT_TYPES } from './types';

const { Money } = sdkTypes;

export const moneyAmount = money => {
  const amount =
    typeof money?.amount === 'number'
      ? money.amount
      : typeof money?.amount?.toString === 'function'
      ? Number.parseInt(money.amount.toString(), 10)
      : null;

  return Number.isInteger(amount) ? amount : null;
};

export const moneyFromValue = value => {
  const amount = moneyAmount(value);
  return Number.isInteger(amount) && value?.currency ? new Money(amount, value.currency) : null;
};

export const getOfferLineItem = transaction => {
  const lineItems = transaction?.attributes?.lineItems || [];
  return lineItems.find(item => LISTING_UNIT_TYPES.includes(item.code) && !item.reversal) || null;
};

const latestMetadataOffer = transaction => {
  const offers = transaction?.attributes?.metadata?.offers;
  return Array.isArray(offers) && offers.length > 0 ? offers[offers.length - 1] : null;
};

export const getOfferQuantity = ({ transaction, listing, cartItem } = {}) => {
  const protectedData = transaction?.attributes?.protectedData || {};
  const offerLineItem = getOfferLineItem(transaction);
  const metadataOffer = latestMetadataOffer(transaction);
  const cartQuantity =
    toPositiveInteger(cartItem?.stockCount) || toPositiveInteger(cartItem?.quantity);
  const lineItemQuantity = toPositiveInteger(offerLineItem?.quantity);
  const listingQuantity = getListingQuantityValue(listing || transaction?.listing, {
    includeStock: false,
  });

  return (
    (cartQuantity && cartQuantity > 1 ? cartQuantity : null) ||
    toPositiveInteger(protectedData.offerQuantity) ||
    toPositiveInteger(protectedData.quantity) ||
    toPositiveInteger(metadataOffer?.quantity) ||
    (lineItemQuantity && lineItemQuantity > 1 ? lineItemQuantity : null) ||
    listingQuantity ||
    cartQuantity ||
    lineItemQuantity ||
    1
  );
};

export const getOfferTotal = ({ transaction, cartItem } = {}) => {
  const offerLineItem = getOfferLineItem(transaction);
  const protectedData = transaction?.attributes?.protectedData || {};
  const metadataOffer = latestMetadataOffer(transaction);
  const currency =
    cartItem?.currency ||
    cartItem?.lineTotal?.currency ||
    cartItem?.price?.currency ||
    offerLineItem?.lineTotal?.currency ||
    offerLineItem?.unitPrice?.currency ||
    transaction?.attributes?.payinTotal?.currency;

  const metadataTotal =
    Number.isInteger(metadataOffer?.offerInSubunits) && currency
      ? new Money(metadataOffer.offerInSubunits, currency)
      : null;
  const protectedTotal =
    Number.isInteger(protectedData.offerTotalInSubunits) && currency
      ? new Money(protectedData.offerTotalInSubunits, currency)
      : null;

  return (
    moneyFromValue(cartItem?.lineTotal) ||
    moneyFromValue(offerLineItem?.lineTotal) ||
    protectedTotal ||
    metadataTotal ||
    moneyFromValue(transaction?.attributes?.payinTotal) ||
    null
  );
};

export const getOfferUnitPrice = ({ transaction, listing, cartItem } = {}) => {
  const quantity = getOfferQuantity({ transaction, listing, cartItem });
  const total = getOfferTotal({ transaction, cartItem });
  const offerLineItem = getOfferLineItem(transaction);
  const explicitUnitPrice =
    moneyFromValue(cartItem?.price) || moneyFromValue(offerLineItem?.unitPrice);

  if (total && quantity > 1) {
    return new Money(Math.round(total.amount / quantity), total.currency);
  }

  return explicitUnitPrice || total;
};

export const getOfferPriceDetails = ({ transaction, listing, cartItem } = {}) => {
  const quantity = getOfferQuantity({ transaction, listing, cartItem });
  const totalPrice = getOfferTotal({ transaction, cartItem });
  const unitPrice = getOfferUnitPrice({ transaction, listing, cartItem });
  const fallbackTotal =
    unitPrice && quantity
      ? new Money(unitPrice.amount * quantity, unitPrice.currency)
      : totalPrice || null;

  return {
    quantity,
    unitPrice,
    totalPrice: totalPrice || fallbackTotal,
  };
};

export const formatUnitPriceFromTotal = (intl, totalPrice, quantity) => {
  const amount = moneyAmount(totalPrice);
  const currency = totalPrice?.currency;
  const parsedQuantity = toPositiveInteger(quantity);

  if (!Number.isInteger(amount) || !currency || !parsedQuantity) {
    return null;
  }

  return new Intl.NumberFormat(intl.locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: currency === 'JPY' ? 0 : 2,
  }).format(amount / 100 / parsedQuantity);
};

export const formatOfferUnitPrice = (intl, totalPrice, quantity, unitPrice) => {
  const derivedUnitPrice = formatUnitPriceFromTotal(intl, totalPrice, quantity);
  return derivedUnitPrice || (unitPrice ? formatMoney(intl, unitPrice) : null);
};
